import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Plus, Trash2, Upload, Eye, Save, GripVertical } from 'lucide-react';

const ICON_OPTIONS = ['wallet', 'trophy', 'chart', 'bitcoin', 'shield', 'arrow', 'zap', 'gift', 'smile', 'dollar'];
const BG_OPTIONS = [{ v: 'none', l: 'None' }, { v: 'image', l: 'Image' }, { v: 'video', l: 'Video URL / MP4' }];

export const AdminLandingPage = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState('brand');
  const [locale, setLocale] = useState('en');
  const [translations, setTranslations] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await adminAPI.getLanding();
      setCfg(res.data);
      setTranslations((res.data.translations && res.data.translations[locale]) || {});
    } catch (e) { toast.error('Failed to load'); }
  };

  const patch = (key, value) => setCfg({ ...cfg, [key]: value });
  const patchNested = (key, sub) => setCfg({ ...cfg, [key]: { ...cfg[key], ...sub } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        brand: cfg.brand, hero: cfg.hero, features: cfg.features,
        how_it_works: cfg.how_it_works, why_us: cfg.why_us,
        download: cfg.download, footer: cfg.footer, sections: cfg.sections,
        tvl: cfg.tvl, maintenance: cfg.maintenance,
        languages: cfg.languages, default_language: cfg.default_language,
      };
      const res = await adminAPI.updateLanding(payload);
      setCfg(res.data);
      toast.success('Saved');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed');
    } finally { setSaving(false); }
  };

  const saveTranslations = async () => {
    try {
      await adminAPI.updateLandingTranslations(locale, translations);
      toast.success(`Translations for "${locale}" saved`);
    } catch (e) { toast.error('Failed'); }
  };

  const uploadAsset = async (kind, file, onDone) => {
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = String(reader.result).split(',')[1];
        const res = await adminAPI.uploadLandingAsset(kind, file.name, file.type, b64);
        onDone(res.data.path);
        toast.success('Uploaded');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (e) { toast.error('Upload failed'); setUploading(false); }
  };

  const moveItem = (list, id, dir) => {
    const idx = list.findIndex((x) => x.id === id);
    if (idx < 0) return list;
    const swap = idx + (dir === 'up' ? -1 : 1);
    if (swap < 0 || swap >= list.length) return list;
    const copy = [...list];
    [copy[idx], copy[swap]] = [copy[swap], copy[idx]];
    return copy.map((x, i) => ({ ...x, order: i + 1 }));
  };

  const removeItem = (list, id) => list.filter((x) => x.id !== id).map((x, i) => ({ ...x, order: i + 1 }));

  const updateItem = (list, id, patchObj) => list.map((x) => x.id === id ? { ...x, ...patchObj } : x);

  const addItem = (list, tpl) => [...list, { ...tpl, id: `${tpl.id_prefix || 'item'}_${Date.now()}`, order: list.length + 1, enabled: true }];

  if (!cfg) {
    return <AdminLayout><div className="p-8 text-center">Loading…</div></AdminLayout>;
  }

  const activeLocaleTranslations = (cfg.translations || {})[locale] || {};

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-landing-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Landing Page</h1>
            <p className="mt-2 text-muted-foreground">Edit every section of your public marketing page. Users hit this at <code>/</code>.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.open('/', '_blank', 'noopener,noreferrer')} data-testid="preview-landing-btn"><Eye className="h-4 w-4 mr-2" /> Preview</Button>
            <Button onClick={save} disabled={saving} data-testid="save-landing-btn"><Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save all'}</Button>
          </div>
        </div>

        {cfg.maintenance?.enabled && (
          <Card className="border-amber-500/50 bg-amber-500/10">
            <CardContent className="pt-4 pb-4 text-sm text-amber-700 dark:text-amber-400">
              ⚠️ Maintenance mode is <strong>ON</strong>. Regular users can&apos;t use the platform until you turn it off in the Maintenance tab.
            </CardContent>
          </Card>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="brand" data-testid="tab-brand">Brand</TabsTrigger>
            <TabsTrigger value="hero" data-testid="tab-hero">Hero</TabsTrigger>
            <TabsTrigger value="features" data-testid="tab-features">Features</TabsTrigger>
            <TabsTrigger value="how" data-testid="tab-how">How it works</TabsTrigger>
            <TabsTrigger value="why" data-testid="tab-why">Why us</TabsTrigger>
            <TabsTrigger value="download" data-testid="tab-download">Download</TabsTrigger>
            <TabsTrigger value="tvl" data-testid="tab-tvl">TVL</TabsTrigger>
            <TabsTrigger value="footer" data-testid="tab-footer">Footer</TabsTrigger>
            <TabsTrigger value="sections" data-testid="tab-sections">Show/Hide</TabsTrigger>
            <TabsTrigger value="lang" data-testid="tab-lang">Languages</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance</TabsTrigger>
          </TabsList>

          {/* BRAND */}
          <TabsContent value="brand" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Brand identity</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="Brand name">
                  <Input value={cfg.brand?.name || ''} onChange={(e) => patchNested('brand', { name: e.target.value })} data-testid="brand-name-input" />
                </Field>
                <Field label="Tagline">
                  <Input value={cfg.brand?.tagline || ''} onChange={(e) => patchNested('brand', { tagline: e.target.value })} data-testid="brand-tagline-input" />
                </Field>
                <Field label="Logo">
                  <div className="flex items-center gap-3">
                    {cfg.brand?.logo_url && <img src={cfg.brand.logo_url.startsWith('http') ? cfg.brand.logo_url : `${process.env.REACT_APP_BACKEND_URL}/${cfg.brand.logo_url}`} alt="logo" className="h-12 w-12 rounded object-cover border" />}
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset('logo', e.target.files?.[0], (path) => patchNested('brand', { logo_url: path }))} />
                      <Button asChild variant="outline"><span><Upload className="h-4 w-4 mr-2" /> {uploading ? 'Uploading…' : 'Upload logo'}</span></Button>
                    </label>
                    <Input value={cfg.brand?.logo_url || ''} onChange={(e) => patchNested('brand', { logo_url: e.target.value })} placeholder="or paste URL" data-testid="brand-logo-input" />
                  </div>
                </Field>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HERO */}
          <TabsContent value="hero" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Hero section</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="Title"><Input value={cfg.hero?.title || ''} onChange={(e) => patchNested('hero', { title: e.target.value })} data-testid="hero-title-input" /></Field>
                <Field label="Description"><Textarea rows={3} value={cfg.hero?.description || ''} onChange={(e) => patchNested('hero', { description: e.target.value })} data-testid="hero-desc-input" /></Field>
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Primary button text"><Input value={cfg.hero?.cta_primary_text || ''} onChange={(e) => patchNested('hero', { cta_primary_text: e.target.value })} /></Field>
                  <Field label="Primary button link"><Input value={cfg.hero?.cta_primary_link || ''} onChange={(e) => patchNested('hero', { cta_primary_link: e.target.value })} /></Field>
                  <Field label="Secondary button text"><Input value={cfg.hero?.cta_secondary_text || ''} onChange={(e) => patchNested('hero', { cta_secondary_text: e.target.value })} /></Field>
                  <Field label="Secondary button link"><Input value={cfg.hero?.cta_secondary_link || ''} onChange={(e) => patchNested('hero', { cta_secondary_link: e.target.value })} /></Field>
                </div>
                <Field label="Background">
                  <Select value={cfg.hero?.bg_type || 'none'} onValueChange={(v) => patchNested('hero', { bg_type: v })}>
                    <SelectTrigger data-testid="hero-bg-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{BG_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                {cfg.hero?.bg_type !== 'none' && (
                  <Field label={cfg.hero?.bg_type === 'video' ? 'Video URL or upload MP4' : 'Image URL or upload'}>
                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer">
                        <input type="file" accept={cfg.hero?.bg_type === 'video' ? 'video/mp4' : 'image/*'} className="hidden" onChange={(e) => uploadAsset('hero', e.target.files?.[0], (path) => patchNested('hero', { bg_url: path }))} />
                        <Button asChild variant="outline"><span><Upload className="h-4 w-4 mr-2" /> Upload</span></Button>
                      </label>
                      <Input value={cfg.hero?.bg_url || ''} onChange={(e) => patchNested('hero', { bg_url: e.target.value })} placeholder="or paste URL" data-testid="hero-bg-url" />
                    </div>
                  </Field>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FEATURES / HOW / WHY - use same list editor */}
          {['features', 'how', 'why'].map((section) => {
            const key = section === 'features' ? 'features' : section === 'how' ? 'how_it_works' : 'why_us';
            const label = section === 'features' ? 'Feature cards' : section === 'how' ? 'How it works steps' : 'Why choose us';
            const items = cfg[key] || [];
            const showIcon = section !== 'how';
            return (
              <TabsContent key={section} value={section} className="mt-6">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle>{label}</CardTitle>
                      <Button size="sm" variant="outline" onClick={() => patch(key, addItem(items, { id_prefix: section, icon: 'wallet', title: 'New item', description: '' }))} data-testid={`add-${section}-btn`}>
                        <Plus className="h-3 w-3 mr-1" /> Add
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {items.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No items yet.</p>}
                    {items.map((it, idx) => (
                      <div key={it.id} className="p-3 rounded-lg border border-border space-y-2" data-testid={`${section}-item-${it.id}`}>
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col">
                            <button type="button" onClick={() => patch(key, moveItem(items, it.id, 'up'))} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30">▲</button>
                            <button type="button" onClick={() => patch(key, moveItem(items, it.id, 'down'))} disabled={idx === items.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30">▼</button>
                          </div>
                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                          <div className="flex-1 grid gap-2 md:grid-cols-3">
                            <Input value={it.title || ''} onChange={(e) => patch(key, updateItem(items, it.id, { title: e.target.value }))} placeholder="Title" />
                            <Input value={it.description || ''} onChange={(e) => patch(key, updateItem(items, it.id, { description: e.target.value }))} placeholder="Description" className="md:col-span-2" />
                            {showIcon && (
                              <Select value={it.icon || 'wallet'} onValueChange={(v) => patch(key, updateItem(items, it.id, { icon: v }))}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{ICON_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                              </Select>
                            )}
                          </div>
                          <Switch checked={it.enabled !== false} onCheckedChange={(v) => patch(key, updateItem(items, it.id, { enabled: v }))} />
                          <Button size="icon" variant="ghost" onClick={() => patch(key, removeItem(items, it.id))} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}

          {/* DOWNLOAD */}
          <TabsContent value="download" className="mt-6">
            <Card>
              <CardHeader><CardTitle>App download section</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3"><Switch checked={!!cfg.download?.enabled} onCheckedChange={(v) => patchNested('download', { enabled: v })} /><Label>Show section</Label></div>
                <Field label="Google Play URL"><Input value={cfg.download?.google_play_url || ''} onChange={(e) => patchNested('download', { google_play_url: e.target.value })} /></Field>
                <Field label="App Store URL"><Input value={cfg.download?.app_store_url || ''} onChange={(e) => patchNested('download', { app_store_url: e.target.value })} /></Field>
                <Field label="APK URL"><Input value={cfg.download?.apk_url || ''} onChange={(e) => patchNested('download', { apk_url: e.target.value })} /></Field>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TVL */}
          <TabsContent value="tvl" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Total Value Locked</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3"><Switch checked={!!cfg.sections?.tvl} onCheckedChange={(v) => patchNested('sections', { tvl: v })} data-testid="toggle-tvl-visibility" /><Label>Show TVL card on landing page</Label></div>
                <Field label="Mode">
                  <Select value={cfg.tvl?.mode || 'off'} onValueChange={(v) => patchNested('tvl', { mode: v })}>
                    <SelectTrigger data-testid="tvl-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">Off</SelectItem>
                      <SelectItem value="live">Live (computed from all wallets + savings)</SelectItem>
                      <SelectItem value="manual">Manual (set a fixed value)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Label"><Input value={cfg.tvl?.label || ''} onChange={(e) => patchNested('tvl', { label: e.target.value })} /></Field>
                {cfg.tvl?.mode === 'manual' && (
                  <div className="grid gap-3 md:grid-cols-2">
                    <Field label="Value"><Input type="number" value={cfg.tvl?.manual_value || 0} onChange={(e) => patchNested('tvl', { manual_value: parseFloat(e.target.value) || 0 })} /></Field>
                    <Field label="Currency"><Input value={cfg.tvl?.manual_currency || ''} onChange={(e) => patchNested('tvl', { manual_currency: e.target.value.toUpperCase() })} placeholder="ZAR" /></Field>
                  </div>
                )}
                {cfg.tvl?.mode === 'live' && (
                  <Field label="Display currency"><Input value={cfg.tvl?.manual_currency || 'ZAR'} onChange={(e) => patchNested('tvl', { manual_currency: e.target.value.toUpperCase() })} placeholder="ZAR" /></Field>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FOOTER */}
          <TabsContent value="footer" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Footer</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="Copyright text"><Input value={cfg.footer?.copyright || ''} onChange={(e) => patchNested('footer', { copyright: e.target.value })} /></Field>
                <Field label="Privacy policy URL"><Input value={cfg.footer?.privacy_url || ''} onChange={(e) => patchNested('footer', { privacy_url: e.target.value })} /></Field>
                <Field label="Terms URL"><Input value={cfg.footer?.terms_url || ''} onChange={(e) => patchNested('footer', { terms_url: e.target.value })} /></Field>
                <Field label="Support URL"><Input value={cfg.footer?.support_url || ''} onChange={(e) => patchNested('footer', { support_url: e.target.value })} /></Field>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SECTIONS toggle */}
          <TabsContent value="sections" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Show / hide sections</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {['hero', 'features', 'how_it_works', 'why_us', 'download', 'tvl', 'footer'].map((k) => (
                  <div key={k} className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <Label className="capitalize">{k.replace(/_/g, ' ')}</Label>
                    <Switch checked={cfg.sections?.[k] !== false} onCheckedChange={(v) => patchNested('sections', { [k]: v })} data-testid={`toggle-section-${k}`} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* LANGUAGES */}
          <TabsContent value="lang" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Languages & translations</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="Supported languages (comma-separated codes, e.g. en,fr,zu)">
                  <Input
                    value={(cfg.languages || ['en']).join(',')}
                    onChange={(e) => patch('languages', e.target.value.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean))}
                    data-testid="languages-input"
                  />
                </Field>
                <Field label="Default language">
                  <Select value={cfg.default_language || 'en'} onValueChange={(v) => patch('default_language', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(cfg.languages || ['en']).map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>

                <div className="pt-4 mt-4 border-t border-border">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <Label>Edit translations for:</Label>
                    <Select value={locale} onValueChange={(v) => { setLocale(v); setTranslations(((cfg.translations || {})[v]) || {}); }}>
                      <SelectTrigger className="w-32" data-testid="translation-lang-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{(cfg.languages || ['en']).map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" onClick={saveTranslations} data-testid="save-translations-btn">Save translations</Button>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Leave a field blank to use the base English text. Keys use the base config as fallback.</p>

                  <TranslationField label="Brand name" k="brand_name" translations={translations} setTranslations={setTranslations} base={cfg.brand?.name} />
                  <TranslationField label="Brand tagline" k="brand_tagline" translations={translations} setTranslations={setTranslations} base={cfg.brand?.tagline} />
                  <TranslationField label="Hero title" k="hero_title" translations={translations} setTranslations={setTranslations} base={cfg.hero?.title} />
                  <TranslationField label="Hero description" k="hero_description" translations={translations} setTranslations={setTranslations} base={cfg.hero?.description} multiline />
                  <TranslationField label="CTA primary" k="hero_cta_primary" translations={translations} setTranslations={setTranslations} base={cfg.hero?.cta_primary_text} />
                  <TranslationField label="CTA secondary" k="hero_cta_secondary" translations={translations} setTranslations={setTranslations} base={cfg.hero?.cta_secondary_text} />
                  <TranslationField label="Footer copyright" k="footer_copyright" translations={translations} setTranslations={setTranslations} base={cfg.footer?.copyright} />
                  <TranslationField label="TVL label" k="tvl_label" translations={translations} setTranslations={setTranslations} base={cfg.tvl?.label} />

                  <p className="text-xs text-muted-foreground mt-4">Feature/how/why titles + descriptions use keys like <code>feature_&lt;id&gt;_title</code>, <code>how_&lt;id&gt;_description</code>, etc. Add them via the API or extend this panel later.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MAINTENANCE */}
          <TabsContent value="maintenance" className="mt-6">
            <Card>
              <CardHeader><CardTitle>Maintenance mode</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 text-sm">
                  When ON, regular users see a maintenance page. Admin users can still sign in and use the admin portal.
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={!!cfg.maintenance?.enabled} onCheckedChange={(v) => patchNested('maintenance', { enabled: v })} data-testid="toggle-maintenance" />
                  <Label>Enable maintenance mode</Label>
                  {cfg.maintenance?.enabled && <Badge className="bg-amber-500 text-white">Active</Badge>}
                </div>
                <Field label="Message shown to users">
                  <Textarea rows={3} value={cfg.maintenance?.message || ''} onChange={(e) => patchNested('maintenance', { message: e.target.value })} data-testid="maintenance-message-input" />
                </Field>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    {children}
  </div>
);

const TranslationField = ({ label, k, translations, setTranslations, base, multiline }) => {
  const value = translations[k] ?? '';
  const set = (v) => setTranslations({ ...translations, [k]: v });
  return (
    <div className="grid gap-1 mb-3 md:grid-cols-3">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground truncate" title={base}>Base: {base || '(none)'}</p>
      </div>
      <div className="md:col-span-2">
        {multiline ? (
          <Textarea rows={2} value={value} onChange={(e) => set(e.target.value)} placeholder="Translation…" data-testid={`translation-${k}`} />
        ) : (
          <Input value={value} onChange={(e) => set(e.target.value)} placeholder="Translation…" data-testid={`translation-${k}`} />
        )}
      </div>
    </div>
  );
};

export default AdminLandingPage;
